/**
 * editor2/hooks/usePaymentEmail.ts
 * PaymentEmailModal の状態管理フック
 * ReservationEditorPanel の paymentEmailModal 関連ロジックを抽出
 */
import { useState, useCallback } from 'react';
import { logEvent } from '../../../lib/eventLog';
import { applyGroupToBody, fetchReservationGroup } from '../../../agent/reservationGroup';
import { localizeBrand } from '../../../config/storeBrand';
import { getStoreKey } from '../../../config/backend';
import { MAIL_FOOTER_TEMPLATES } from '../constants';
import { getSquareIndex } from '../../../config/backend';
import type { EditorReservation } from '../types';
import type { PaymentEmailState } from '../modals/PaymentEmailModal';
import { INITIAL_PAYMENT_EMAIL_STATE } from '../modals/PaymentEmailModal';
import { runAiEmailTurn, AiEmailVersion, AiChatEntry, AiEmailOutput } from '../aiEmail';
import type { GeminiContent } from '../../../agent/types';

const INITIAL_STATE: PaymentEmailState = INITIAL_PAYMENT_EMAIL_STATE;

const LANG_LABELS: Record<string, string> = {
  ja: '日本語',
  en: 'English',
  tw: '繁體中文',
  kr: '한국어',
};

/** 予約データを AI へ渡すための整形（人が読める Markdown） */
function formatReservationForAi(data: EditorReservation): string {
  const lines: string[] = ['## 予約情報'];
  const push = (label: string, value: any) => {
    if (value == null || value === '') return;
    lines.push(`- ${label}: ${value}`);
  };
  push('顧客名', data.customer_name);
  push('予約コード', data.contact_code || data.contact_id || data.id);
  push('貸出日時', data.start_date);
  push('返却日時', data.end_date);
  push('車種', data.car_type);
  push('基本料金', data.base_price != null ? `¥${Number(data.base_price).toLocaleString()}` : '');
  push(
    '合計金額',
    data.sum_price != null ? `¥${Number(data.sum_price).toLocaleString()}` : '',
  );
  push('メールアドレス', data.customer_email);
  push('電話番号', data.customer_tel);
  push('大人', data.pax);
  push('子供', data.pax_children);
  push('お客様コメント', data.comment);
  return lines.join('\n');
}

interface UsePaymentEmailOptions {
  getData: () => EditorReservation;
  sysApiBase: string;
}

function getMailHeader(data: EditorReservation, lang: string): string {
  const name = data.customer_name || 'お客様';
  const code = data.contact_code || data.contact_id || data.id || '';

  let greeting = `${name} 様`;
  if (lang === 'en') greeting = `Dear ${name}`;
  if (lang === 'tw') greeting = `${name} 您好`;
  if (lang === 'kr') greeting = `${name} 고객님`;

  let label = '予約コード';
  if (lang === 'en') label = 'Reservation Code';
  if (lang === 'tw') label = '預約編號';
  if (lang === 'kr') label = '예약 코드';

  return `${greeting}\n${label}: ${code}\n\n`;
}

function getMailFooter(lang: string): string {
  return MAIL_FOOTER_TEMPLATES[lang] || MAIL_FOOTER_TEMPLATES['ja'];
}

function formatDate(dateStr: string, lang: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    if (lang === 'en') return `${y}/${m}/${day} ${h}:${min}`;
    if (lang === 'tw') return `${y}年${m}月${day}日 ${h}:${min}`;
    if (lang === 'kr') return `${y}년 ${m}월 ${day}일 ${h}:${min}`;
    return `${y}年${m}月${day}日 ${h}:${min}`;
  } catch { return dateStr; }
}

function formatPrice(price: number | null | undefined, lang: string): string {
  if (price == null) return '';
  const n = Number(price);
  if (isNaN(n)) return String(price);
  if (lang === 'en') return `¥${n.toLocaleString()}`;
  if (lang === 'tw') return `¥${n.toLocaleString()}`;
  if (lang === 'kr') return `¥${n.toLocaleString()}`;
  return `¥${n.toLocaleString()}`;
}

// --- リクエスト系テンプレート ---
function buildRequestOk(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);
  const price = formatPrice(data.sum_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nWe are pleased to inform you that the vehicle is available for your requested dates.\nPlease reply by 12:00 noon tomorrow to confirm your reservation.\n\nWe look forward to hearing from you.\n\n■Reservation Details\nPickup: ${start}\nReturn: ${end}\nTotal: ${price}\n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n我們很高興通知您，您所希望的日期已確認有車輛可供使用。\n請在明天中午12點之前回覆，以正式確認您的預約。\n\n期待收到您的回覆。\n\n■預約內容\n取車日期: ${start}\n還車日期: ${end}\n總金額: ${price}\n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 차량 확보가 가능하여 연락드립니다.\n내일 12시까지 회신해 주시면 정식으로 예약이 확정됩니다.\n\n바쁘시겠지만 연락 부탁드립니다.\n\n■예약 내용\n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: ${price}\n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\n\nご希望の日程にて在庫の確保ができましたので、ご連絡いたしました。\n明日の12時までにご返信をいただけますと、正式に予約確定となります。\n\nお忙しいところ恐縮ですが、ご連絡をお待ちしております。\n\n■予約内容\n貸出日時: ${start}\n返却日時: ${end}\n合計金額: ${price}\n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

/**
 * リクエスト（深夜早朝）OK（2026-09-26 追加）。
 * 深夜早朝帯（営業時間外）の貸出・返却リクエストに対応できる場合の回答。
 * 文面は request_ok と同じ構成で、2段落目だけ「深夜早朝帯の対応が可能」にする（前田さん指定の文面）
 */
function buildRequestOkNight(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);
  const price = formatPrice(data.sum_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nWe are pleased to inform you that we can accommodate your request during the late-night / early-morning hours on your requested dates.\nPlease reply by 12:00 noon tomorrow to confirm your reservation.\n\nWe look forward to hearing from you.\n\n■Reservation Details\nPickup: ${start}\nReturn: ${end}\nTotal: ${price}\n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n您所希望的日期，我們可以在深夜・清晨時段為您提供服務。\n請在明天中午12點之前回覆，以正式確認您的預約。\n\n期待收到您的回覆。\n\n■預約內容\n取車日期: ${start}\n還車日期: ${end}\n總金額: ${price}\n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 심야·이른 아침 시간대 대응이 가능합니다.\n내일 12시까지 회신해 주시면 정식으로 예약이 확정됩니다.\n\n바쁘시겠지만 연락 부탁드립니다.\n\n■예약 내용\n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: ${price}\n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja（前田さん指定の文面。宛名・予約コードのヘッダと署名フッタは呼び出し側で付く）
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\n\nご希望の日程にて深夜早朝帯の対応が可能でございます。\n明日の12時までにご返信をいただけますと、正式に予約確定となります。\n\nお忙しいところ恐縮ですが、ご連絡をお待ちしております。\n\n■予約内容\n貸出日時: ${start}\n返却日時: ${end}\n合計金額: ${price}\n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

/**
 * リクエストを在庫ありで**即確定**したときの確定のご連絡（2026-10-13 記録#40）。
 * オファー（明日12時までに返信を）を経ないので、返信のお願いは書かない。
 * 日本語の本文は前田さんの指示文のとおり。
 */
function buildRequestBooked(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);
  const price = formatPrice(data.sum_price, lang);
  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Your reservation is confirmed',
      body: `Thank you for your reservation. We are pleased to inform you that your reservation has been confirmed.\n\n■Reservation Details\nPickup: ${start}\nReturn: ${end}\nTotal: ${price}\n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 預約確認通知',
      body: `感謝您的預約。我們已為您確認預約，特此通知。\n\n■預約內容\n取車日期: ${start}\n還車日期: ${end}\n總金額: ${price}\n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 예약 확정 안내',
      body: `예약해 주셔서 감사합니다. 예약이 확정되었음을 안내드립니다.\n\n■예약 내용\n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: ${price}\n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  return {
    subject: 'Nレンタカーです。ご予約が確定いたしました。',
    body: `ご予約いただきありがとうございます。予約を確定いたしましたのでご連絡いたします。\n\n■予約内容\n貸出日時: ${start}\n返却日時: ${end}\n合計金額: ${price}\n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

function buildRequestFull(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);
  const price = formatPrice(data.sum_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nUnfortunately, we were unable to secure a vehicle for your requested dates.\nWe sincerely apologize for the inconvenience.\nWe look forward to serving you in the future.\n\n■Requested Reservation\nPickup: ${start}\nReturn: ${end}\nTotal: ${price}\n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n很遺憾，您所希望的日期無法確保車輛。\n非常抱歉造成不便。\n期待下次為您服務。\n\n■預約請求內容\n取車日期: ${start}\n還車日期: ${end}\n總金額: ${price}\n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 차량 확보가 불가능했습니다.\n대단히 죄송합니다.\n다음 기회에 이용해 주시기를 기다리겠습니다.\n\n■리퀘스트 예약 내용\n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: ${price}\n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\nご希望の日程ですが在庫が確保できませんでした。\n大変申し訳ございません。\nまたのご予約をお待ちしております。\n\n■リクエスト予約内容\n貸出日時: ${start}\n返却日時: ${end}\n合計金額: ${price}\n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

function buildCancel(_data: EditorReservation, lang: string): { subject: string; body: string } {
  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Cancellation Confirmation',
      body: `This is N RENT A CAR Naha Airport.\nWe have received your cancellation.\n\nWe are sorry to see you go and look forward to serving you again in the future.`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 取消確認',
      body: `N RENT A CAR 那霸機場店。\n我們已收到您的取消申請。\n\n非常遺憾，期待下次再為您服務。`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 취소 안내',
      body: `N 렌터카 나하 공항점입니다.\n취소가 접수되었습니다.\n\n대단히 아쉽지만, 다음 기회에 다시 이용해 주시기를 기다리겠습니다.`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。キャンセルを承りました。',
    body: `Nレンタカー 那覇空港店でございます。\nキャンセルを承りました。\n\n大変残念ではございますが、又のご利用をお待ちしております。`,
  };
}

function buildCancelFee(data: EditorReservation, lang: string): { subject: string; body: string } {
  const basePrice = formatPrice(data.base_price, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Cancellation and Cancellation Fee',
      body: `This is N RENT A CAR Naha Airport.\nWe have received your cancellation.\n\nWe regret to inform you that, in accordance with our cancellation policy, a cancellation fee will apply.\nBase price: ${basePrice}\nCancellation fee: [calculated amount]\n\nPlease let us know about this cancellation when making your next reservation.\nWe will offer you a discount.\n\nWe look forward to serving you again.`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 取消及取消費用通知',
      body: `N RENT A CAR 那霸機場店。\n我們已收到您的取消申請。\n\n非常抱歉，根據取消政策，將向您收取取消費用。\n基本費用: ${basePrice}\n取消費用: [計算金額]\n\n下次預約時請告知此次取消情況，我們將為您提供折扣。\n\n期待您再次光臨。`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 취소 및 취소 수수료 안내',
      body: `N 렌터카 나하 공항점입니다.\n취소가 접수되었습니다.\n\n대단히 죄송하지만, 취소 정책에 따라 취소 수수료가 부과됩니다.\n기본 요금: ${basePrice}\n취소 수수료: [계산된 금액]\n\n다음 예약 시 이번 취소 건에 대해 말씀해 주시면 할인을 적용해 드리겠습니다.\n\n다음 기회에 다시 이용해 주시기를 기다리겠습니다.`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。キャンセル料についてご連絡いたします。',
    body: `Nレンタカー 那覇空港店でございます。\nキャンセルを承りました。\n\n大変恐縮ではございますが、キャンセルポリシーに基づきキャンセル料をご請求いたします。\n基本料金: ${basePrice}\nキャンセル料金: [計算した料金]\n\n次回ご予約の際に、今回キャンセルとなった旨をお知らせください。\n割引をさせていただきます。\n\n又のご利用をお待ちしております。`,
  };
}

function buildRequestOffer(data: EditorReservation, lang: string): { subject: string; body: string } {
  const start = formatDate(data.start_date, lang);
  const end = formatDate(data.end_date, lang);

  if (lang === 'en') {
    return {
      subject: '[N RENT A CAR] Thank you for your reservation request',
      body: `Thank you for your reservation request.\n\nUnfortunately, we were unable to prepare the requested vehicle type for your dates.\nWe would like to offer an alternative vehicle. Please consider the following:\n\n■Alternative Offer\nVehicle: \nPickup: ${start}\nReturn: ${end}\nTotal: \n\nThank you.\n\nN RENT A CAR Naha Airport`,
    };
  }
  if (lang === 'tw') {
    return {
      subject: '[N RENT A CAR] 感謝您的預約請求',
      body: `感謝您提出預約請求。\n\n很遺憾，您所希望的日期無法準備所請求的車型。\n我們提供以下替代方案，請您考慮。\n\n■替代方案\n車型: \n取車日期: ${start}\n還車日期: ${end}\n總金額: \n\n謝謝。\n\nN RENT A CAR 那霸機場店`,
    };
  }
  if (lang === 'kr') {
    return {
      subject: '[N RENT A CAR] 리퀘스트 예약 감사합니다',
      body: `리퀘스트 예약을 해주셔서 진심으로 감사드립니다.\n\n희망하시는 일정에 희망 차종을 준비할 수 없었습니다.\n다른 차종으로 제안드립니다. 검토 부탁드립니다.\n\n■제안 내용\n차종: \n대여 일시: ${start}\n반납 일시: ${end}\n합계 금액: \n\n감사합니다.\n\nN 렌터카 나하 공항점`,
    };
  }
  // ja
  return {
    subject: 'Nレンタカーです。リクエスト予約ありがとうございます。',
    body: `この度は、リクエスト予約をいただき誠にありがとうございます。\n\nご希望日時にご希望の車種をご用意することができませんでした。\n別の車種でのご提案でございます。ご検討をお願い致します。\n\n■ご提案内容\n車種: \n貸出日時: ${start}\n返却日時: ${end}\n合計金額: \n\nよろしくお願いいたします。\n\nNレンタカー 那覇空港店`,
  };
}

export default function usePaymentEmail({ getData, sysApiBase }: UsePaymentEmailOptions) {
  const [modalState, setModalState] = useState<PaymentEmailState>(INITIAL_STATE);

  const openPaymentEmail = useCallback(
    (type: string = 'payment') => {
      const data = getData();
      // ai_compose は言語選択をスキップして直接 ai_compose ステップへ
      const initialStep: PaymentEmailState['step'] =
        type === 'ai_compose' ? 'ai_compose' : 'lang';
      setModalState({
        ...INITIAL_STATE,
        open: true,
        step: initialStep,
        type,
        to: data.customer_email || '',
      });
    },
    [getData],
  );

  const handleLangSelect = useCallback(
    async (lang: string) => {
      const data = getData();
      const mode = modalState.type;
      const header = getMailHeader(data, lang);
      const footer = `\n\n${getMailFooter(lang)}`;
      let subject = '';
      let body = '';

      if (mode === 'free') {
        // Free-form email with just header + footer
        if (lang === 'en') {
          subject = '[N RENT A CAR] Information';
          body = `${header}This is N RENT A CAR.${footer}`;
        } else if (lang === 'tw') {
          subject = '[N RENT A CAR] 通知';
          body = `${header}N RENT A CAR 那霸機場店${footer}`;
        } else if (lang === 'kr') {
          subject = '[N RENT A CAR] 안내';
          body = `${header}N 렌터카 나하 공항점입니다.${footer}`;
        } else {
          subject = '[N RENT A CAR] ご案内';
          body = `${header}Nレンタカー 那覇空港店でございます。${footer}`;
        }

        setModalState((prev) => ({ ...prev, step: 'preview', lang, subject: localizeBrand(subject), body: localizeBrand(body) }));
        return;
      }

      if (mode === 'comment') {
        const custComment = data.comment || '';
        if (lang === 'en') {
          subject = '[N RENT A CAR] Inquiry Response';
          body = `${header}This is N RENT A CAR Naha Airport.\nThank you for your inquiry.\n\n◼︎Comment\n${custComment}\n\n→[Reply to comment]\n\nPlease feel free to contact us if you have any questions.\nThank you.\n\nN RENT A CAR Naha Airport${footer}`;
        } else if (lang === 'tw') {
          subject = '[N RENT A CAR] 問題回覆';
          body = `${header}N RENT A CAR 那霸機場店\n感謝您的諮詢。\n\n◼︎留言\n${custComment}\n\n→[留言回覆]\n\n若有任何問題，歡迎隨時與我們聯繫。\n感謝您的配合。\n\nN RENT A CAR 那霸機場店${footer}`;
        } else if (lang === 'kr') {
          subject = '[N RENT A CAR] 문의 내용 안내';
          body = `${header}N 렌터카 나하 공항점입니다.\n문의해 주셔서 진심으로 감사드립니다.\n\n◼︎코멘트\n${custComment}\n\n→[코멘트 답변]\n\n궁금한 점이 있으시면 언제든지 편하게 문의해 주시기 바랍니다.\n감사합니다.\n\nN 렌터카 나하 공항점${footer}`;
        } else {
          subject = '[N RENT A CAR] お問い合わせ内容について';
          body = `${header}Nレンタカー 那覇空港店でございます。\nこの度は、お問い合わせをいただきまして、誠にありがとうございます。\n\n◼︎コメント\n${custComment}\n\n→[コメントへの返信]\n\nまた何かございましたらご気軽にいつでもお問い合わせください。\n何卒よろしくお願いいたします。\n\nNレンタカー 那覇空港店${footer}`;
        }

        setModalState((prev) => ({ ...prev, step: 'preview', lang, subject: localizeBrand(subject), body: localizeBrand(body) }));
        return;
      }

      // リクエスト系・キャンセル系テンプレート（フロントで生成）
      if (
        mode === 'request_ok' ||
        mode === 'request_ok_night' ||
        mode === 'request_full' ||
        mode === 'request_offer' ||
        mode === 'cancel' ||
        mode === 'cancel_fee'
      ) {
        const builder = mode === 'request_ok' ? buildRequestOk
          : mode === 'request_ok_night' ? buildRequestOkNight
          : mode === 'request_full' ? buildRequestFull
          : mode === 'request_offer' ? buildRequestOffer
          : mode === 'cancel' ? buildCancel
          : buildCancelFee;
        const tpl = builder(data, lang);
        subject = tpl.subject;
        body = `${header}${tpl.body}${footer}`;
        // 複数台予約（枝番 -1, -2 …）なら全台分の合計に書き換える（2026-11-03: 10/22 に3台の予約へ1台分の合計を送った）
        try {
          const group = await fetchReservationGroup({ contact_code: data.contact_code, start_date: data.start_date });
          if (group) body = applyGroupToBody(body, group, String((data as any).car_type ?? ''));
        } catch (e: any) {
          logEvent({ action: 'mail.template.group_total', target: String(data.contact_code ?? ''), result: 'fail', err: String(e?.message || e) });
        }

        setModalState((prev) => ({ ...prev, step: 'preview', lang, subject: localizeBrand(subject), body: localizeBrand(body) }));
        return;
      }

      // payment: fetch from API
      try {
        const response = await fetch(`${sysApiBase}/get_payment_email_preview.php`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lang,
            customer_name: data.customer_name,
            contact_code: data.contact_code || data.contact_id || data.id,
            page_id: data.page_id || data.id,
            license: !!data.license,
            agreement: !!data.agreement,
            // 店舗別のブランド・連絡先で生成させる（省略すると那覇固定になる）。
            // 実際の送信は webhook が tokenIndex から解決するので、プレビューと一致する。
            sqNum: getSquareIndex(),
          }),
        });
        const result = await response.json();

        subject = result.subject || '[N RENT A CAR] Payment Confirmation';
        if (lang === 'ja' && !subject.includes('支払い確認'))
          subject = '[N RENT A CAR] 支払い確認のお願い';

        body = `${header}${result.body}${footer}`;
      } catch (error) {
        console.error('Failed to fetch payment email preview', error);
        alert('メールテンプレートの取得に失敗しました');
        return;
      }

      setModalState((prev) => ({ ...prev, step: 'preview', lang, subject: localizeBrand(subject), body: localizeBrand(body) }));
    },
    [getData, modalState.type, sysApiBase],
  );

  const handleSend = useCallback(() => {
    if (!modalState.to) {
      setModalState((prev) => ({ ...prev, error: '送信先メールアドレスがありません' }));
      return null;
    }

    const emailData = {
      to: modalState.to,
      // CC/BCC は任意。空なら送らない(サーバ側でもヘッダを付けない)
      ...(modalState.cc?.trim() ? { cc: modalState.cc.trim() } : {}),
      ...(modalState.bcc?.trim() ? { bcc: modalState.bcc.trim() } : {}),
      subject: modalState.subject,
      body: modalState.body,
    };

    // Close immediately
    setModalState((prev) => ({ ...prev, open: false, sending: false }));
    return emailData;
  }, [modalState]);

  const closePaymentEmail = useCallback(() => {
    setModalState((prev) => ({ ...prev, open: false }));
  }, []);

  /** バージョン履歴エントリの生成 */
  const makeVersion = (
    instruction: string,
    subject: string,
    body: string,
    meta: AiEmailVersion['meta'],
  ): AiEmailVersion => ({
    id: `v_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
    createdAt: new Date().toISOString(),
    instruction,
    subject,
    body,
    meta,
  });

  const makeChatId = () => `c_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

  /** AI 応答から対話スレッド用の吹き出しエントリを作る */
  const makeAiChatEntry = (output: AiEmailOutput, versionId?: string): AiChatEntry => {
    const isReply = !!output.reply && !output.body;
    return {
      id: makeChatId(),
      role: 'ai',
      kind: isReply ? 'reply' : 'draft-update',
      text: isReply ? output.reply : (output.meta.reasoning || '本文を更新しました。'),
      versionId,
      assumptions: isReply ? [] : output.meta.assumptions,
      checkpoints: isReply ? [] : output.meta.checkpoints,
      toolLog: output.meta.toolLog,
    };
  };

  /**
   * AI改善（Track C2: マルチターン化）
   * - 会話履歴（aiConversation）を保持し、過去の指示・判断の文脈を保ったまま改善する
   * - テンプレート起点のメール（会話履歴なし）は、初回に予約情報 + 現在のメールを文脈として与える
   * - 出力は生成と同じ JSON スキーマ（reasoning 付き）
   */
  const handleAiImprove = useCallback(async () => {
    const { body, subject, aiInstruction, lang, aiImproving, aiConversation } = modalState;
    const instruction = aiInstruction.trim();
    if (aiImproving || !body.trim() || !instruction) return;

    const langLabel = LANG_LABELS[lang] || '日本語';
    const data = getData();
    // 会話が無い（テンプレート起点）場合は予約情報を文脈として同梱
    const contextBlock = aiConversation.length === 0
      ? `${formatReservationForAi(data)}\n\n`
      : '';
    // スタッフが手修正している可能性があるため、常に現在のメールを明示して渡す
    const userMessage = [
      contextBlock,
      '## 現在のメール（スタッフが手修正している場合があります）',
      `件名: ${subject}`,
      `本文:\n${body}`,
      `（本文の言語: ${langLabel}）`,
      '',
      '## スタッフからのメッセージ',
      instruction,
    ].join('\n');

    // 指示は即座に吹き出しとして表示（送信感を出す）
    const userEntry: AiChatEntry = { id: makeChatId(), role: 'user', text: instruction };
    setModalState((prev) => ({
      ...prev,
      aiImproving: true,
      error: '',
      aiInstruction: '',
      aiChat: [...prev.aiChat, userEntry],
    }));

    try {
      const turnContents: GeminiContent[] = [
        ...aiConversation,
        { role: 'user', parts: [{ text: userMessage }] },
      ];
      const { output, appendedContents } = await runAiEmailTurn(turnContents);
      const isReply = !!output.reply && !output.body;
      if (!output.body && !output.reply) {
        throw new Error('AIの出力に本文も返答も含まれていませんでした');
      }
      if (isReply) {
        // 対話返答のみ: メールは変更せず、会話とスレッドだけ進める
        setModalState((prev) => ({
          ...prev,
          aiConversation: [...turnContents, ...appendedContents],
          aiChat: [...prev.aiChat, makeAiChatEntry(output)],
          aiImproving: false,
        }));
        return;
      }
      setModalState((prev) => {
        const version = makeVersion(instruction, output.subject || prev.subject, output.body, output.meta);
        return {
          ...prev,
          subject: output.subject || prev.subject,
          body: output.body,
          aiMeta: output.meta,
          aiConversation: [...turnContents, ...appendedContents],
          aiHistory: [...prev.aiHistory, version],
          aiChat: [...prev.aiChat, makeAiChatEntry(output, version.id)],
          aiImproving: false,
        };
      });
    } catch (e: any) {
      console.error('AI improve failed', e);
      setModalState((prev) => ({
        ...prev,
        aiImproving: false,
        error: `AI改善に失敗しました: ${e?.message || e}`,
        aiChat: [...prev.aiChat, { id: makeChatId(), role: 'ai', kind: 'reply', text: `⚠️ エラー: ${e?.message || e}` }],
      }));
    }
  }, [modalState, getData]);

  /**
   * AIで作成（Track C1+C3: reasoning 付き JSON 出力 + 読み取り専用 tool による根拠の接地）
   */
  const handleAiCompose = useCallback(async () => {
    const { composePrompt, aiComposing } = modalState;
    const userPrompt = composePrompt.trim();
    if (aiComposing || !userPrompt) return;

    const data = getData();
    const reservationBlock = formatReservationForAi(data);
    const userMessage = `${reservationBlock}\n\n## 生成指示\n${userPrompt}`;

    setModalState((prev) => ({ ...prev, aiComposing: true, error: '' }));

    try {
      const turnContents: GeminiContent[] = [{ role: 'user', parts: [{ text: userMessage }] }];
      const { output, appendedContents } = await runAiEmailTurn(turnContents);
      if (!output.body) throw new Error('AIの出力に本文が含まれていませんでした');
      const subject = localizeBrand(output.subject || '[N RENT A CAR] ご案内');
      const version = makeVersion(userPrompt, subject, output.body, output.meta);
      setModalState((prev) => ({
        ...prev,
        step: 'preview',
        subject,
        body: output.body,
        aiMeta: output.meta,
        aiConversation: [...turnContents, ...appendedContents],
        aiHistory: [version],
        aiChat: [
          { id: makeChatId(), role: 'user', text: userPrompt },
          makeAiChatEntry(output, version.id),
        ],
        aiComposing: false,
      }));
    } catch (e: any) {
      console.error('AI compose failed', e);
      setModalState((prev) => ({
        ...prev,
        aiComposing: false,
        error: `AI生成に失敗しました: ${e?.message || e}`,
      }));
    }
  }, [modalState, getData]);

  /** 生成履歴からの差し戻し（Track C2） */
  const restoreVersion = useCallback((versionId: string) => {
    setModalState((prev) => {
      const v = prev.aiHistory.find((h) => h.id === versionId);
      if (!v) return prev;
      return { ...prev, subject: v.subject, body: v.body, aiMeta: v.meta };
    });
  }, []);

  return {
    modalState,
    setModalState,
    openPaymentEmail,
    handleLangSelect,
    handleSend,
    closePaymentEmail,
    handleAiImprove,
    handleAiCompose,
    restoreVersion,
  };
}

// ============================================================
// mail-queue からの再利用エクスポート（2026-08-07）
// サイドパネルの定型テンプレ（リクエスト系・キャンセル系）を /mail-queue でも
// 使えるようにする。ヘッダ（宛名+予約コード）とフッタ（署名）込みで生成する。
// 正: booking-platform 側 md/master_md/feature-mail-queue.md「テンプレ選択」
// ============================================================
/** 店舗で使える定型テンプレ（宮古には深夜早朝の対応がないので request_ok_night を除く。2026-10-03） */
export function availableBuiltinTemplates(): { key: string; label: string }[] {
  return BUILTIN_MAIL_TEMPLATES.filter((t) => !(t.key === 'request_ok_night' && getStoreKey() === 'pit'));
}

export const BUILTIN_MAIL_TEMPLATES: { key: string; label: string }[] = [
  { key: 'request_ok', label: 'リクエスト OK（在庫確保）' },
  { key: 'request_ok_night', label: 'リクエスト（深夜早朝）OK' },
  { key: 'request_booked', label: '予約確定（在庫ありで即確定）' },
  { key: 'request_full', label: 'リクエスト full（満車）' },
  { key: 'request_offer', label: 'リクエスト offer（代替提案）' },
  { key: 'cancel', label: 'キャンセル承り（通常）' },
  { key: 'cancel_fee', label: 'キャンセル承り（C料金）' },
];

export function buildBuiltinMail(
  type: string,
  data: EditorReservation,
  lang: string,
): { subject: string; body: string } | null {
  const builder =
    type === 'request_ok' ? buildRequestOk
    : type === 'request_ok_night' ? buildRequestOkNight
    : type === 'request_booked' ? buildRequestBooked
    : type === 'request_full' ? buildRequestFull
    : type === 'request_offer' ? buildRequestOffer
    : type === 'cancel' ? buildCancel
    : type === 'cancel_fee' ? buildCancelFee
    : null;
  if (!builder) return null;
  const tpl = builder(data, lang);
  return {
    // 文面は那覇の表記で書かれている → 宮古の画面なら宮古の表記に（2026-10-03）
    subject: localizeBrand(tpl.subject),
    body: localizeBrand(`${getMailHeader(data, lang)}${tpl.body}\n\n${getMailFooter(lang)}`),
  };
}
